import moment from "moment";
import React, { useEffect, useRef } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { FormProvider, useForm } from "react-hook-form";
import { connect, useDispatch } from "react-redux";
import updatePersonalProfileAction from "redux/actions/updatePersonalProfileAction";

import { getPersonalInformationAction } from "redux/actions/auth/getPersonalInformationAction";
import { getMultipleSelectOptionsElementByCode, getSelectOptionsElementByCode } from "utils/formSelect";

// @material-ui/core components
import { Container } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import PasswordForm from "../Components/PasswordForm";

//custom components
import GenericBackdrop from "components/Backdrops/GenericBackdrop";
import GenericButton from "components/Buttons/GenericButton";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import CompanyForm from "./CompanyForm";
import UserForm from "./UserForm";

import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import componentStylesUserProfile from "assets/theme/views/admin/user-profile.js";
import LocationHeader from "components/Headers/LocationHeader";
import { dataSetsNeededColectiveRegistration } from "constants/componentsDatasets";
import useQueryParams from "hooks/useQueryParams";
import userProfileEnterpriseUpdateMap from "mappers/UserProfileEnterpriseUpdateMap";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { getDataSetByNameAction } from "redux/actions/datasetsAction";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { DATASET, normalizeAOIBAN } from "utils/const";
import { FetchDatasetsOnMount } from "utils/datasets";
import UserPicture from "../Components/UserPicture";
import WorkCenters from "./WorkCenters/WorkCenters";
import WorkCentersEmployees from "./WorkCentersEmployees/WorkCentersEmployees";
import CooperativeMembers from "./CooperativeMembers/CooperativeMembers";

const useStylesGeneric = makeStyles(componentStylesGeneric);
const useStylesUserProfile = makeStyles(componentStylesUserProfile);

function UserProfile(props) {
  FetchDatasetsOnMount(props.getDataSetByName, props.datasetsState, dataSetsNeededColectiveRegistration);

  const classes = { ...useStylesGeneric(), ...useStylesUserProfile() };
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [file, setFile] = React.useState();
  const [tabType, settabType] = React.useState("user");
  const [changePassword, setChangePassword] = React.useState(false);

  const dispatch = useDispatch();

  const showDialog = (message, submessage, yesAction, noAction) => {
    dispatch(showGenericTwoButtonDialogAction(message, submessage, yesAction, noAction));
  };

  const location = useLocation();

  const { getQueryParams, setQueryParams } = useQueryParams();
  const didApplyStateTabRef = useRef(false);

  const isCooperative = props.authState?.personalInformation?.userColective?.userColectiveTypeId === 2;

  const normalizeTabType = (value) => {
    if (!value) return null;

    if (value === "employees" || value === "WorkCentersEmployees") return "WorkCentersEmployees";
    if (value === "workCenter") return "workCenter";
    if (value === "company") return "company";
    if (value === "user") return "user";
    if (value === "cooperativeMembers") return "cooperativeMembers";

    return null;
  };

  const inferTabFromParams = (params) => {
    const keys = Object.keys(params || {});
    if (keys.some((k) => k.startsWith("wce_"))) return "WorkCentersEmployees";
    if (keys.some((k) => k.startsWith("wc_"))) return "workCenter";
    return "user";
  };

  const setTabInUrl = (nextTab) => {
    const currentParams = getQueryParams();
    const updatedParams = { ...currentParams };

    if (!nextTab || nextTab === "user") {
      delete updatedParams.tab;
    } else {
      updatedParams.tab = nextTab;
    }

    setQueryParams(updatedParams, true);
  };

  const methods = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    const stateTab = location?.state?.tabType;
    if (didApplyStateTabRef.current) return;
    if (stateTab === undefined) return;

    const normalized = normalizeTabType(stateTab) || "user";
    settabType(normalized);
    setTabInUrl(normalized);
    didApplyStateTabRef.current = true;
  }, [location?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = getQueryParams();
    const tabFromUrl = normalizeTabType(params?.tab);

    if (tabFromUrl) {
      settabType(tabFromUrl);
      return;
    }

    const inferred = inferTabFromParams(params);
    settabType(inferred);
    if (inferred !== "user") {
      setTabInUrl(inferred);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = getQueryParams();
    const tabFromUrl = normalizeTabType(params?.tab);

    if (tabFromUrl && tabFromUrl !== tabType) {
      settabType(tabFromUrl);
      return;
    }

    if (!tabFromUrl) {
      const inferred = inferTabFromParams(params);
      if (inferred !== tabType) {
        settabType(inferred);
      }
      if (inferred !== "user") {
        setTabInUrl(inferred);
      }
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    props.getPersonalInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #region SETVALUES
  React.useEffect(() => {
    if (!props.authState.personalInformation_loading) {
      methods.setValue("companyName", props.authState.personalInformation?.name);
      methods.setValue("contactName", props.authState.personalInformation?.userColective?.contactName);
      methods.setValue("alternativeMobilePhone", props.authState.personalInformation?.alternativeMobilePhone);
      methods.setValue("mobilePhone", props.authState.personalInformation?.mobilePhone);
      methods.setValue("mobilePhoneConfirmation", props.authState.personalInformation?.mobilePhone);
      methods.setValue("email", props.authState.personalInformation?.email);
      methods.setValue("emailConfirmation", props.authState.personalInformation?.email);
      methods.setValue("taxIdNumber", props.authState.personalInformation?.userColective?.taxIdNumber);
      methods.setValue("permitCode", props.authState.personalInformation?.userColective?.permitCode);
      methods.setValue(
        "permitCodeValidUntil",
        props.authState.personalInformation?.userColective?.permitCodeValidUntil
          ? moment(props.authState.personalInformation.userColective.permitCodeValidUntil)
          : ""
      );
      methods.setValue("district", props.authState.personalInformation?.userColective?.compositeAddress?.district);
      methods.setValue("address", props.authState.personalInformation?.userColective?.compositeAddress?.address);
      methods.setValue("niss", props.authState.personalInformation?.userColective?.niss);
      methods.setValue("companyEmail", props.authState.personalInformation?.userColective?.companyEmail);
      methods.setValue("companyPhone", props.authState.personalInformation?.userColective?.companyPhone);
      methods.setValue("experience", props.authState.personalInformation?.userColective?.experience);
      methods.setValue("propertyType", props.authState.personalInformation?.userColective?.propertyType);
      methods.setValue("subsidiaryCompany", props.authState.personalInformation?.userColective?.subsidiaryCompany);
      methods.setValue(
        "subsidiaryCompanyPostalCode",
        props.authState.personalInformation?.userColective?.subsidiaryCompanyPostalCode
      );
      methods.setValue(
        "subsidiaryCompanyActivity",
        props.authState.personalInformation?.userColective?.subsidiaryCompanyActivity
      );
      methods.setValue("maleWorkersNumber", props.authState.personalInformation?.userColective?.maleWorkersNumber);
      methods.setValue("femaleWorkersNumber", props.authState.personalInformation?.userColective?.femaleWorkersNumber);
      methods.setValue(
        "disableWorkersNumber",
        props.authState.personalInformation?.userColective?.disableWorkersNumber
      );
      methods.setValue(
        "bankAccountNumber",
        normalizeAOIBAN(props.authState.personalInformation?.userColective?.bankAccountNumber ?? "")
      );
      methods.setValue("totalWorkersNumber", props.authState.personalInformation?.userColective?.totalWorkersNumber);
      methods.setValue("shareCapital", props.authState.personalInformation?.userColective?.shareCapital);
      methods.setValue(
        "publicDivisionPercentage",
        props.authState.personalInformation?.userColective?.publicDivisionPercentage
      );
      methods.setValue(
        "privateNationalDivisionPercentage",
        props.authState.personalInformation?.userColective?.privateNationalDivisionPercentage
      );
      methods.setValue(
        "activityStartYear",
        props.authState.personalInformation?.userColective?.activityStartYear
          ? moment({ year: props.authState.personalInformation.userColective.activityStartYear })
          : ""
      );

      methods.setValue("corporateObject", props.authState.personalInformation?.userColective?.corporateObject);
      methods.setValue(
        "IsStartupOrMicroenterprise",
        props.authState.personalInformation?.userColective?.IsStartupOrMicroenterprise ??
          props.authState.personalInformation?.userColective?.isStartupOrMicroenterprise ??
          false
      );
    }

    setupDropdowns(props, methods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.authState.personalInformation_loading]);
  // #endregion

  //#region PROVINCES
  React.useEffect(() => {
    if (props.datasetsState && props.datasetsState[DATASET.PROVINCES]) {
      methods.setValue(
        "province",
        getSelectOptionsElementByCode(
          props.datasetsState[DATASET.PROVINCES],
          props.authState.personalInformation?.userColective?.compositeAddress?.provinceId
        )
      );

      const provinceKey = props.datasetsState[DATASET.PROVINCES].find(
        (el) => el.code === props.authState.personalInformation?.userColective?.compositeAddress?.provinceId
      )?.keyValue;
      if (provinceKey) props.getDataSetByName(DATASET.MUNICIPALITIES, provinceKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.PROVINCES]]);
  //#endregion

  //#region MUNICIPALITIES
  React.useEffect(() => {
    if (props.datasetsState) {
      methods.setValue(
        "municipality",
        // getDatasetStateByName(props, DATASET.MUNICIPALITIES)
        getSelectOptionsElementByCode(
          props.datasetsState[DATASET.MUNICIPALITIES],
          props.authState.personalInformation?.userColective?.compositeAddress?.municipalityId
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.MUNICIPALITIES]]);
  //#endregion

  //#region BUSINESS_PURPOSE
  React.useEffect(() => {
    if (props.datasetsState) {
      methods.setValue(
        "businessPurpose",
        getMultipleSelectOptionsElementByCode(
          props.datasetsState[DATASET.BUSINESS_PURPOSE],
          props.authState.personalInformation?.userColective?.businessPurposeList
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.BUSINESS_PURPOSE]]);
  //#endregion

  //#region ECONOMIC_ACTIVITIES_CLASSIFICATION
  React.useEffect(() => {
    if (props.datasetsState) {
      methods.setValue(
        "companyEACId",
        getSelectOptionsElementByCode(
          props.datasetsState[DATASET.ECONOMIC_ACTIVITIES_CLASSIFICATION],
          props.authState.personalInformation?.userColective?.companyEACId
        )
      );

      methods.setValue(
        "subsidiaryCompanyEACId",
        getSelectOptionsElementByCode(
          props.datasetsState[DATASET.ECONOMIC_ACTIVITIES_CLASSIFICATION],
          props.authState.personalInformation?.userColective?.subsidiaryCompanyEACId
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.ECONOMIC_ACTIVITIES_CLASSIFICATION]]);
  //#endregion

  //#region LEGALFORM
  React.useEffect(() => {
    if (props.datasetsState) {
      methods.setValue(
        "legalFormId",
        getSelectOptionsElementByCode(
          props.datasetsState[DATASET.LEGAL_FORM],
          props.authState.personalInformation?.userColective?.legalFormId
        )
      );
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.LEGAL_FORM]]);
  //#endregion

  const handletabType = (event, value) => {
    if (value !== null) {
      settabType(value);
      setTabInUrl(value);
    }
  };

  function handleChangePassword() {
    setChangePassword(true);
  }

  function handleCloseModal() {
    setChangePassword(false);
  }

  const handleRegister = (data) => {
    const mappedData = userProfileEnterpriseUpdateMap(data, file, props.authState?.personalInformation?.id);
    showDialog(
      "Editar Os Dados do Utilizador",
      "Tem a certeza que deseja editar os dados do utilizador?",
      () => props.updatePersonalProfile(mappedData),
      () => {}
    );
  };

  return (
    <>
      <GenericBackdrop open={props.authState.personalInformation_loading} />
      <FormProvider {...methods}>
        <Container maxWidth={false} component={Box} marginTop="2.5rem" classes={{ root: classes.containerRoot }} disableGutters>
          <LocationHeader section="Meu Perfil" />
          <Grid container>
            <Grid item xs={12} md={3} className={classes.userProfileTitle}>
              Meu perfil
            </Grid>
            <Grid
              item
              xs={12}
              md={9}
              style={{
                display: "flex",
                justifyContent: isSmDown ? "stretch" : "flex-end",
                alignItems: "flex-start",
              }}
            >
              <ToggleButtonGroup
                value={tabType}
                exclusive
                onChange={handletabType}
                aria-label="entity type"
                orientation={isSmDown ? "vertical" : "horizontal"}
                className={classes.changeTabsGroupUserProfile}
              >
                <ToggleButton
                  value="user"
                  aria-label="user"
                  className={classes.changeTabs + " " + classes.changeTabsUserProfile}
                >
                  Dados Utilizador
                </ToggleButton>
                <ToggleButton
                  value="company"
                  aria-label="company"
                  className={classes.changeTabs + " " + classes.changeTabsUserProfile}
                >
                  Dados Entidade e Palavra-Passe
                </ToggleButton>
                <ToggleButton
                  value="workCenter"
                  aria-label="workCenter"
                  className={classes.changeTabs + " " + classes.changeTabsUserProfile}
                >
                  Centros de Trabalho
                </ToggleButton>
                <ToggleButton
                  value="WorkCentersEmployees"
                  aria-label="WorkCentersEmployees"
                  className={classes.changeTabs + " " + classes.changeTabsUserProfile}
                >
                  Trabalhadores
                </ToggleButton>
                {isCooperative && (
                  <ToggleButton
                    value="cooperativeMembers"
                    aria-label="cooperativeMembers"
                    className={classes.changeTabs + " " + classes.changeTabsUserProfile}
                  >
                    Membros da Cooperativa
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          {tabType === "company" || tabType === "user" ? (
            <form onSubmit={methods.handleSubmit(handleRegister)}>
              <Grid container>
                <Grid item xs={12} md={3}>
                  {tabType === "user" ? <UserPicture file={file} setFile={setFile} /> : null}
                </Grid>
                {tabType === "user" ? (
                  <Grid item xs={12} md={9}>
                    <UserForm />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={12}>
                    <CompanyForm handleChangePassword={handleChangePassword} />
                  </Grid>
                )}
              </Grid>
              <Grid container>
                <Grid item xs={12} md={12}>
                  <Box className={classes.boxButtonSaveProfile}>
                    <GenericButton
                      typeSubmit={true}
                      color="primary"
                      loading={props.personalProfileState.update_profile_loading}
                      size={isSmDown ? "medium" : undefined}
                      fullWidth={isSmDown}
                    >
                      Guardar
                    </GenericButton>
                  </Box>
                </Grid>
              </Grid>
            </form>
          ) : (
            <Grid container>
              {tabType === "workCenter" ? (
                <Grid item xs={12} md={12}>
                  <WorkCenters />
                </Grid>
              ) : tabType === "WorkCentersEmployees" ? (
                <Grid item xs={12} md={12}>
                  <WorkCentersEmployees />
                </Grid>
              ) : tabType === "cooperativeMembers" && isCooperative ? (
                <Grid item xs={12} md={12}>
                  <CooperativeMembers
                    cooperativeId={props.authState?.personalInformation?.id}
                  />
                </Grid>
              ) : null}
            </Grid>
          )}
        </Container>
      </FormProvider>

      <GenericDialog maxWidth={"sm"} open={changePassword} onClose={handleCloseModal}>
        <PasswordForm handleCloseModal={handleCloseModal} />
      </GenericDialog>
    </>
  );
}

const mapStateToProps = (state) => ({ ...state });
const mapDispatchToProps = (dispatch) => ({
  updatePersonalProfile: (data) => dispatch(updatePersonalProfileAction(data)),
  getPersonalInformation: () => dispatch(getPersonalInformationAction()),
  getDataSetByName: (name, addEmptyField) => dispatch(getDataSetByNameAction(name, addEmptyField)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserProfile);

function setupDropdowns(props, methods) {
  /* #region  Province */
  if (props.datasetsState && props.datasetsState[DATASET.PROVINCES]) {
    const provinceId = props.authState.personalInformation?.userColective?.compositeAddress?.provinceId;

    methods.setValue("province", getSelectOptionsElementByCode(props.datasetsState[DATASET.PROVINCES], provinceId));

    const provinceKey = props.datasetsState[DATASET.PROVINCES].find(
      (el) => el.code === props.authState.personalInformation?.userColective?.compositeAddress?.provinceId
    )?.keyValue;
    if (provinceKey) props.getDataSetByName(DATASET.MUNICIPALITIES, provinceKey);
  }
  /* #endregion */

  /* #region  Municipality */
  if (props.datasetsState && props.datasetsState[DATASET.MUNICIPALITIES]) {
    methods.setValue(
      "municipality",
      // getDatasetStateByName(props, DATASET.MUNICIPALITIES)
      getSelectOptionsElementByCode(
        props.datasetsState[DATASET.MUNICIPALITIES],
        props.authState.personalInformation?.userColective?.compositeAddress?.municipalityId
      )
    );
  }

  /* #endregion */

  /* #region Business Purpose */
  if (props.datasetsState && props.datasetsState[DATASET.BUSINESS_PURPOSE]) {
    methods.setValue(
      "businessPurpose",
      getMultipleSelectOptionsElementByCode(
        props.datasetsState[DATASET.BUSINESS_PURPOSE],
        props.authState.personalInformation?.userColective?.businessPurposeList
      )
    );
  }
  /* #endregion */

  /* #region Business Purpose */
  if (props.datasetsState && props.datasetsState[DATASET.ECONOMIC_ACTIVITIES_CLASSIFICATION]) {
    methods.setValue(
      "companyEACId",
      getSelectOptionsElementByCode(
        props.datasetsState[DATASET.ECONOMIC_ACTIVITIES_CLASSIFICATION],
        props.authState.personalInformation?.userColective?.companyEACId
      )
    );

    methods.setValue(
      "subsidiaryCompanyEACId",
      getSelectOptionsElementByCode(
        props.datasetsState[DATASET.ECONOMIC_ACTIVITIES_CLASSIFICATION],
        props.authState.personalInformation?.userColective?.subsidiaryCompanyEACId
      )
    );
  }
  /* #endregion */

  /* #region Business Purpose */
  if (props.datasetsState && props.datasetsState[DATASET.LEGAL_FORM]) {
    methods.setValue(
      "legalFormId",
      getSelectOptionsElementByCode(
        props.datasetsState[DATASET.LEGAL_FORM],
        props.authState.personalInformation?.userColective?.legalFormId
      )
    );
  }
  /* #endregion */

  /* #region User Collective Type */
  if (props.datasetsState && props.datasetsState[DATASET.USER_COLLECTIVE_TYPE]) {
    const mappedOptions = props.datasetsState[DATASET.USER_COLLECTIVE_TYPE].map((item) => ({
      code: item.code ?? item.id,
      label: item.label ?? item.value ?? item.keyValue ?? "",
      keyValue: item.keyValue ?? item.value,
    }));
    methods.setValue(
      "userColectiveTypeId",
      getSelectOptionsElementByCode(
        mappedOptions,
        props.authState.personalInformation?.userColective?.userColectiveTypeId
      )
    );
  }
  /* #endregion */
}
